import { useState } from 'react';
import { Alert, FormDialog, FormField, Rating, Textarea } from '@nexvia-logistics/ui';
import { describeStorageError } from '@/shared/storage-api';

/** nexvia-api-web `storage.schemas.ts` `STORAGE_REVIEW_COMMENT_MAX`. */
const COMMENT_MAX = 1000;

/** Same words as web-app's `StarRatingDialog` (rating a driver), so both sides read the stars alike. */
const RATING_LABELS: Record<number, string> = {
  1: 'Muy malo',
  2: 'Malo',
  3: 'Regular',
  4: 'Bueno',
  5: 'Excelente'
};

interface ClientReviewDialogProps {
  /** Gets the stars (1–5) and the trimmed comment (`null` when empty). Throws to show the error here. */
  onSubmit: (review: { rating: number; comment: string | null }) => Promise<void>;
  onCancel: () => void;
}

/**
 * NEX-1329 (decision A6) — "Calificá al cliente": the stars are required, the comment optional
 * (≤1000). Follows web-app's `StarRatingDialog` for rating a driver (the design system's `Rating`,
 * the five words under it, "Ahora no"), on this panel's `FormDialog`. A review cannot be edited, so
 * it is sent from a dialog rather than inline; the API's refusals (`REVIEW_ALREADY_SUBMITTED` from
 * another tab, `STORAGE_REVIEW_NOT_ENABLED`…) show here in Spanish, through `describeStorageError`.
 */
export function ClientReviewDialog({ onSubmit, onCancel }: ClientReviewDialogProps) {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (rating < 1) return;
    setError(null);
    setSubmitting(true);
    try {
      await onSubmit({ rating, comment: comment.trim() || null });
    } catch (err) {
      setError(describeStorageError(err, 'No pudimos guardar tu calificación. Intentá de nuevo.'));
      setSubmitting(false);
    }
  };

  return (
    <FormDialog
      open
      onClose={onCancel}
      onSubmit={handleSubmit}
      title="Calificá al cliente"
      submitLabel="Enviar calificación"
      cancelLabel="Ahora no"
      submitting={submitting}
      submitDisabled={rating < 1}
    >
      <p className="m-0 text-ui-body text-on-surface-variant">
        ¿Cómo fue el cliente durante el guardado? Tu calificación ayuda a que la comunidad de Nexvia elija mejor. Se
        envía una sola vez.
      </p>

      {error && <Alert tone="critical">{error}</Alert>}

      <div className="flex flex-col items-center gap-2">
        <Rating value={rating} onChange={setRating} label="Calificación" size={40} />
        <p className="m-0 min-h-6 text-ui-body text-on-surface-variant">
          {RATING_LABELS[rating] ?? 'Elegí de 1 a 5 estrellas'}
        </p>
      </div>

      <FormField label="Comentario (opcional)" counter={{ value: comment.length, max: COMMENT_MAX }}>
        {(field) => (
          <Textarea
            {...field}
            value={comment}
            onChange={(event) => setComment(event.target.value)}
            maxLength={COMMENT_MAX}
            rows={3}
            placeholder="Ej: fue puntual con la entrega y el retiro."
          />
        )}
      </FormField>
    </FormDialog>
  );
}
